import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ResultMessageType } from "@multiweb/types/contracts";
import { IFrameContext } from "@multiweb/types/multiweb";
import { ErrorHandler } from "UI/Errors";
import { IHasFrameContext } from "UI/UI/UI/Controls/ControlInterfaces";
import { DataConverter } from "../../../../../../../Global/Base/Converters/DataConverter";
import { DigitalSignatureState, ISignatureInfo } from "../../../../../../../Global/Base/Crypto/ISignatureInfo";
import { ISignatureProviderProfile } from "../../../../../../../Global/Base/Crypto/ISignatureProvider";
import { IEncAttrValue } from "../../../../../../../Global/Base/DataTypes/IAttrValue";
import { IListener } from "../../../../../../../Global/Base/Events";
import { CancellationToken } from "../../../../../../../Global/Base/Promises/CancellationTokenSource";
import { Deferred, PromiseUtils } from "../../../../../../../Global/Base/Promises/PromiseUtils";
import { EMPTY_ITEM_RIGHTS_COLLECTION, RightType } from "../../../../../../../Global/Base/Rights/RightTypes";
import { FunctionUtils } from "../../../../../../../Global/Base/Utils/FunctionUtils";
import { StringUtils } from "../../../../../../../Global/Base/Utils/StringUtils";
import { EOWException } from "../../../../../../../OW.Remoting/Basic/OWExceptions";
import { OWDocumentDescription } from "../../../../../../../OW.Remoting/Remoting/DataTypes/OWDocumentDescription";
import { OWObject } from "../../../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { FileUploader, UploadCancelledException } from "../../../../../../../OW.Remoting/Remoting/FileUploader";
import { InstanceManager } from "../../../../../../../OW.Remoting/Remoting/InstanceManager";
import { OWManager } from "../../../../../../../OW.Remoting/Remoting/OWManager";
import { RequestContext } from "../../../../../../../OW.Remoting/Remoting/RequestContext";
import { GenericControl } from "../../../../../../UI.BaseControls/GenericControl";
import { Column } from "../../../../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { DataRow } from "../../../../../../UI.Controls/DataGridSubcontrols/Components/DataRow";
import { createElement, registerCustomElement } from "../../../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ComponentRightsExtractor } from "../../../../../Rights/ComponentRightsExtractor";
import { FileEdit, ICanSignParams } from "../../../../Controls/FileEdit";
import { IUserMsgsData, UserMsgsBuilder } from "../../../../DataBuilders/UserMsgsBuilder";
import { FileEditProgressStatus, FileEditStatus, FileMenuNonEdit, MenuItemFile } from "../../../../FileMenu";
import { CryptoInfo, SignatureValidationMsg } from "../../../../Helpers/CryptoInfo";
import { FileEditCore, IFileEdit } from "../../../../Helpers/FileEditCore";
import { MessageWindow } from "../../../../MessageWindow";
import { UIManager } from "../../../../UIManager";
import { UploadManager } from "../../../../UploadManager";
import { CryptoLabelAction } from "../../../Actions/CryptoLabelAction";
import { FileColumn } from "../FileColumn";
import { InlineBtnWithLoaderBuilder } from "./InlineBtnWithLoaderBuilder";

const fileEditLogger = LazyLoadingSectionLogger.create(LoggingSection.fileEdit);

export class FileCellElement extends HTMLDivElement implements IHasFrameContext {
    private _archiveDocumentId: string;
    private _progressElement: HTMLElement;
    private _nameElement: HTMLElement;
    private _menuBtn: HTMLElement;
    private _signStatusBtn: InlineBtnWithLoaderBuilder;
    private _allowSignWithoutEdit: boolean;
    private _column: Column;
    private _uploader: FileUploader;
    private _uploaderListenerProgress: IListener;
    private _uploaderListenerCancel: IListener;
    private _uploaderListenerStart: IListener;
    private _uploaderListenerError: IListener;
    private _uploaderListenerFinished: IListener;
    private _isLocked: boolean;
    private _actualObject: OWObject;
    private _signInfoBtnClickListener: IListener;
    private _signatureInfo: ISignatureInfo;
    private _fileEditStatus: FileEditStatus;
    private _disposed: boolean = false;
    private _objectAttributeChangeListener: (sender: OWObject, prm: string) => void;
    private _fileMenuNonEdit: FileMenuNonEdit;
    private _attributeData: OWDocumentDescription;
    private _deferredActualObject: Deferred<void> = PromiseUtils.defer();
    private _fileEditCore: FileEditCore;
    private _instanceId: string;
    private _file: File;
    private _signatureProviderProfile: ISignatureProviderProfile | null | undefined = undefined;
    private _digitalSignatureState: DigitalSignatureState;
    protected _isSigningInProgress: boolean = false;

    public frameContext: IFrameContext | undefined = undefined;

    //#region Properties
    public set visible(v: boolean) {
        this.classList.toggle("hidden", !v);
    }

    public get visible(): boolean {
        return !this.classList.contains("hidden");
    }

    public set enabled(v: boolean) {
        this.classList.toggle("disabled", !v);
    }

    public get enabled(): boolean {
        return !this.classList.contains("disabled");
    }

    /**
     * Název zobrazovaného atributu
     */
    public get attrName(): string {
        return this.dataset["attrname"];
    }

    public set attrName(v: string) {
        this.dataset["attrname"] = v;
    }

    private set _signingInProgress(val: boolean) {
        this._isSigningInProgress = val;
    }

    /**
     * Sloupec, ve kterém je tento element
     */
    public set column(v: Column) {
        this._column = v;
    }

    /**
    * Sloupec, ve kterém je tento element
    */
    public get column(): Column {
        return this._column;
    }
    //#endregion Properties

    /**
     * Zobrazovaný název souboru
     */
    public set fileName(v: string) {
        const changed = this.title !== v;

        if (this._isLocked)
            return;

        this._nameElement.textContent = v;
        this.title = v;
        if (v.trim().length === 0) {
            this.setStatus(FileEditStatus.empty);
        } else if (changed) {
            this.setStatus(FileEditStatus.download);
        }
    }
    private get _fileName(): string {
        return this._nameElement.textContent;
    }

    private set _setSignatureProviderProfile(val: ISignatureProviderProfile | null | undefined) {
        this._signatureProviderProfile = val;
    }

    private async _setInstanceId(instanceId: string): Promise<void> {
        try {
            this._file = null;

            if (this._instanceId !== instanceId) {
                this._instanceId = instanceId;
                // při změně instance se registruje naslouchání událostem uploadu
                const uploaderData = UploadManager.instance.registerUpload(instanceId, this.attrName, undefined, this._getRequestContext());
                this._uploader = uploaderData.uploader;
                this._registerUploadListeners();

                if (this._uploader != null) {
                    this._checkStatus();
                }
                if (this._objectAttributeChangeListener) {
                    this._actualObject.attributeChanged.remove(this._objectAttributeChangeListener);
                    this._objectAttributeChangeListener = null;
                }

                this._actualObject = InstanceManager.getInstance(instanceId);
                this._updateFileEditObject();
                if (!this._actualObject) {
                    fileEditLogger.warn("Unable to add attributeChanged listener - _actualObject is undefined");
                }
                this._actualObject?.attributeChanged.add(this._objectAttributeChangeListener = async (_obj, attrName) => {
                    try {
                        if (this.attrName.toUpperCase() !== attrName.toUpperCase() /*|| this._isSigningInProgress*/)
                            return;
                        if (this._signStatusBtn)
                            await this._signStatusBtn.update();
                    } catch (e) {
                        PromiseUtils.cancellationCatch(e);
                    }
                });
                if (instanceId && instanceId !== "undefined") {
                    await this._initializeSignStatusBtn(instanceId);
                } else {
                    fileEditLogger.warn("Unable to initialize sign status button because instanceId is undefined");
                }
            } else {
                this._checkStatus();
                if (this._signStatusBtn)
                    await this._signStatusBtn.update();
            }
            this._deferredActualObject.resolve();
        } catch (e) {
            this._deferredActualObject.reject(e);
        }
    }

    private async _updateFileEditObject(): Promise<void> {
        if (this._fileEditCore) {
            this._fileEditCore.actualObject = this._actualObject;
        }
    }
    private async _initializeSignStatusBtn(_instanceId: string): Promise<void> {
        //inicializuje tlačítko s informací o stavu podpisu
        if (!this._signStatusBtn) {
            const cryptoLabel = new CryptoLabelAction({
                element: createElement({ classes: [""] }),
                originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION,
            });

            //kliknutí na cryptolabel zobrazí tooltip s podrobnými informacemi o podpisu
            cryptoLabel.element.addEventListener("click", this._signInfoBtnClickListener = async (e: MouseEvent) => {
                if (this._signatureInfo && this._signStatusBtn && this._signatureInfo.validationMessage !== SignatureValidationMsg.Empty) {
                    const dropDown = await CryptoInfo.getCryptoInfoDropDown({ signatureInfo: this._signatureInfo }, this._actualObject, this.attrName);
                    dropDown.show(this._signStatusBtn.element, e);
                }
            });

            this._signStatusBtn = new InlineBtnWithLoaderBuilder(cryptoLabel.element, true); //vytvoří btn a začne s tím že zobrazí loader

            this._signStatusBtn.updateAction = FunctionUtils.debouncedFunctionAsync((element) => this._updateSignStatusBtn(element, cryptoLabel), 200);

            if (this._menuBtn)
                this.insertBefore(await this._signStatusBtn.build(), this._menuBtn);
            else
                this.appendChild(await this._signStatusBtn.build());
            await this._signStatusBtn.update();
        } else {
            await this._signStatusBtn.update();
        }
    }

    private _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return {
            roles: this.frameContext?.roles,
            getTransactionContextId: this.frameContext?.getTransactionContextId,
            cancellationToken,
        };
    }

    private async _updateSignStatusBtn(element: HTMLElement, cryptoLabel: CryptoLabelAction): Promise<void> {
        const actualObject = this._fileEditCore && this._fileEditCore.actualObject ? this._fileEditCore.actualObject : this._actualObject;
        this._signatureInfo = await OWManager.getDocumentSignatureInfo(actualObject.instanceID, this.attrName, this._getRequestContext());
        this._signStatusBtn.element.style.display = this._signatureInfo == null || this._signatureInfo.validationMessage === SignatureValidationMsg.EmptySignature ? "none" : "";
        if (this._instanceId !== actualObject.instanceID) { //instanceID bylo změněno, získaný podpis se nepoužije
            this._signStatusBtn.element.style.display = "none";
            element.setAttribute("title", "");
            return;
        }
        cryptoLabel.updateSignedState(this._signatureInfo); //počká na načtení statusu
        element.setAttribute("title", await CryptoInfo.getCryptoInfoToolTip(this._signatureInfo));
    }

    private async _updateMetadata(): Promise<void> {
        let value = await this._actualObject.getValue(this.attrName);
        if (!value) {
            this.fileName = "";
            this._attributeData = null;
            this._archiveDocumentId = null;
            return;
        }
        if (value instanceof IEncAttrValue && !value.isEncrypted)
            value = value.valueOf();
        if (value instanceof OWDocumentDescription)
            this._attributeData = value;

        // Načte informace o archivní verzi dokumentu, aby podle toho bylo možné zobrazit akce v menu
        const metadata = await this._attributeData.getMetadata();
        this._archiveDocumentId = metadata.ArchiveDocumentID;
        const isActionEnabled = !StringUtils.isNullOrEmpty(this._archiveDocumentId);
        this._fileMenuNonEdit.setMenuItemEnabled(isActionEnabled, MenuItemFile.downloadArchiveVersion);
    }

    /**
     * Zjistí, jestli je povoleno záznam podepsat a vyhodnotí visible položky menu pro podpis bez editace
     * @param signatureInfo typu ISignatureInfo
     */
    private async _updateCanSign(signatureInfo?: any): Promise<void> {
        this._digitalSignatureState = FileEdit.getSignatureState(signatureInfo as ISignatureInfo);
        const params: ICanSignParams = {
            actualObject: this._actualObject,
            attrName: this.attrName,
            digitalSignatureState: this._digitalSignatureState,
            isComponentEmpty: this._fileEditStatus === FileEditStatus.empty,

            rightsCollection: ComponentRightsExtractor.loadRightsForTypes(this.column as FileColumn, RightType.useDigitalSign),

            allowCounterSignature: DataConverter.toBoolean(this.column.getAttribute("data-allowcountersignature"), true),
            allowSignWithoutEdit: this._allowSignWithoutEdit,
            fileName: this._nameElement.textContent,
        };
        let signingEnabled = false;

        if (this._allowSignWithoutEdit)
            signingEnabled = await FileEdit.checkSigningAvailable(params, this._getRequestContext());

        if (this._fileMenuNonEdit)
            this._fileMenuNonEdit.updateSigningEnabled(signingEnabled);
    }

    /**
     * Řádek, ve kterém je tento element
     */
    private get _row(): DataRow {
        return GenericControl.findParent(this as HTMLElement, (el) => el.classList.contains("DataRow")) as DataRow;
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        GenericControl._constructor(this as HTMLElement);
    }

    public initialize(column: Column): void {
        this.column = column;
        this.classList.add("fileEditCell", "fileTemplate");
        this._allowSignWithoutEdit = DataConverter.toBoolean(column.getAttribute("data-allowsignwithoutedit"), true);

        this._progressElement = createElement({ classes: "progress" });
        this._nameElement = createElement({ type: "span", classes: "name" });
        this._menuBtn = createElement({ type: "div", classes: ["Icon", "valuepicker"] }); //"inline-btn"

        this._menuBtn.addEventListener("click", async (e: MouseEvent) => {
            if (this._fileMenuNonEdit && this._fileMenuNonEdit.isOpened) {
                this._fileMenuNonEdit.close(false, event);
                return;
            }

            this._row.selectWithCheckChanges(false);

            if (!this._fileMenuNonEdit) {
                this._fileMenuNonEdit = new FileMenuNonEdit(); //Vytvoří needitační menu
                this._initFileMenu();
            }
            this._fileMenuNonEdit.showEmpty(this._menuBtn, e); // Zobrazí prázdné dropdownmenu s loaderem

            await this._deferredActualObject.promise; //počká, než se dočte actual object

            this._updateFileEditObject();

            await this._fileEditCore.updateDownloadUrl(false); // Stáhne odkaz na soubor

            await this._updateMetadata();
            await this._updateCanSign(this._signatureInfo);

            this._fileMenuNonEdit.show(this._menuBtn, e); // Zavře prázdné dropdownmenu s loaderem a otevře needitační menu
        });

        this.appendChild(this._progressElement);
        this.appendChild(this._nameElement);
        this.appendChild(this._menuBtn);
        this.attrName = column.attrName;
        this.column = column;
    }

    /**
     * Vytvoří FileMenu a zaregistruje jeho události
     */
    private _initFileMenu(): void {
        const fileEditAdapter: IFileEdit = {
            getFileName: () => this._fileName,
            getArchiveDocumentId: () => this._archiveDocumentId,
            getLocalFile: () => this._file,
            getSignatureProviderProfile: () => this._signatureProviderProfile,
            getDigitalSignatureState: () => this._digitalSignatureState,
            getControlValue: () => null,
            setSignatureProviderProfile: (value) => this._setSignatureProviderProfile = value,
            setSigningInProgress: (value: boolean) => this._signingInProgress = value,
            updateCanSign: (param: any) => this._updateCanSign(param),
            setValid: () => {
                // do nothnig.
            },
            setInvalid: () => {
                // do nothnig.
            },
            setStatus: (status: FileEditStatus) => this.setStatus(status),
            changeProgressStatus: (_type?: FileEditProgressStatus, value?: number) => this.changeProgressStatus(value),
            fileEditElement: null,
        };

        this._fileEditCore = new FileEditCore(fileEditLogger, fileEditAdapter, this.attrName, this);

        this._fileMenuNonEdit.registerEventListener("downloadRequest", (event: CustomEvent) => {
            this._fileEditCore.downloadFile(event.detail ? event.detail.isArchiveDocument : false);
        });

        this._fileMenuNonEdit.registerEventListener("copyUrl", () => this._fileEditCore.copyUrl());

        this._fileMenuNonEdit.registerEventListener("showVersions", async () => await this._fileEditCore.showArchiveVersions(this));

        this._fileMenuNonEdit.registerEventListener("signRequest", async () => {
            UIManager.instance.showLoader();
            try {
                await this._createSignatureWithoutEdit();
                UIManager.instance.hideLoader();
            } catch (e) {
                UIManager.instance.hideLoader();
                if (e instanceof EOWException) {
                    const saveMessages: IUserMsgsData[] = [];
                    saveMessages.push({ objectIdentification: (this._actualObject ? this._actualObject.objectIdentity : null), messages: [{ type: ResultMessageType.error, message: e.message }] });
                    const mw = new MessageWindow(new UserMsgsBuilder(saveMessages));
                    await mw.show();
                } else
                    ErrorHandler.instance.handleError(e);
            }
        });
    }

    private async _createSignatureWithoutEdit(): Promise<void> {
        await this._fileEditCore.createSignature(undefined, false);
        this._actualObject.isChanged = true; //musí zůstat nastaveno, jinak applyUpdates neuloží změnu na serveru.
        await this._actualObject.applyUpdates();
        this._actualObject.attributeChanged.trigger(this._actualObject, this.attrName);
        await this._signStatusBtn.update();
    }

    protected connectedCallback(): void {
        // při attachnutí je potřeba najít id instance zobrazovaného objektu
        // pokud je id už nastavené, tak se vezme rovnou
        const iid = this._row.getAttribute("data-instanceid");
        if (iid) {
            if (InstanceManager.getInstance(iid)) {
                this._setInstanceId(iid);
            } else {
                fileEditLogger.warn("Unable to set instanceID - OWObject instance is null.");
            }
        }

        // a naslouchá se dalším změnám instancí na vybraném řádku (např. zrušení změn, reload, ...)
        GenericControl.registerEventListener(this._row as HTMLElement, "attributechange", (e: CustomEvent) => {
            if (e.detail.attrName === "data-instanceid" && e.detail.newVal) {
                this._setInstanceId(e.detail.newVal);
            }
        });
    }

    protected disconnectedCallback(): void {
        this.dispose();
    }

    protected attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        GenericControl.attributeChangedCallback(this as HTMLElement, attrName, oldVal, newVal);
    }
    /**
     * Změní vizuální stav komponenty
     * @param status nový stav
     */
    public setStatus(status: FileEditStatus): void {
        this.classList.remove(
            FileEditStatus[FileEditStatus.download],
            FileEditStatus[FileEditStatus.error],
            FileEditStatus[FileEditStatus.uploading],
            FileEditStatus[FileEditStatus.empty]);

        this.classList.add(FileEditStatus[status]);
        this._fileEditStatus = status;
    }

    /**
     * Změní hodnotu progresu nahrávání
     * @param value
     */
    public changeProgressStatus(value: string | number): void {
        this._progressElement.style.width = value != null ? value + "%" : "100%";
    }

    /**
     * Zkontroluje současný stav uploadu a podle toho nastaví komponentu
     */
    private _checkStatus(): void {
        if (!this._uploader.isStarted) {
            return;
        }

        if (!this._uploader.isComplete) {
            this.fileName = this._uploader.fileName;
            this.setStatus(FileEditStatus.uploading);
            this.changeProgressStatus(this._uploader.currentProgress || 0);
        } else if (this._uploader.isError) {
            this.fileName = this._uploader.fileName;
            this.setStatus(FileEditStatus.error);
        } else {
            this.setStatus(FileEditStatus.download);
        }

        this._isLocked = this._uploader.isActive;
    }

    private _registerUploadListeners(): void {
        this._unregisterUploadListeners();

        this._uploader.onStart.add(this._uploaderListenerStart = (sender: FileUploader) => {
            this.changeProgressStatus(0);
            this.setStatus(FileEditStatus.uploading);
            this.fileName = sender.fileName;
            this._isLocked = true;
        });

        this._uploader.onProgress.add(this._uploaderListenerProgress = (_sender: FileUploader, progress: number) => {
            this.changeProgressStatus(progress);
        });

        this._uploader.onError.add(this._uploaderListenerError = (_sender: FileUploader, _ex: Error) => {
            this.setStatus(FileEditStatus.error);
        });

        this._uploader.onFinished.add(this._uploaderListenerFinished = async (_sender: FileUploader, _documentId: string) => {
            if (this._uploader.finishDelay)
                await PromiseUtils.delay(this._uploader.finishDelay);

            this.setStatus(FileEditStatus.download);
            this._isLocked = false;

            if (this._fileEditCore)
                await this._fileEditCore.updateDownloadUrl(false); // Stáhne odkaz na soubor
        });

        this._uploader.onCancel.add(this._uploaderListenerCancel = (_sender: FileUploader, _exception: UploadCancelledException) => {
            this.setStatus(FileEditStatus.download);
            this._isLocked = false;
        });
    }

    private _unregisterUploadListeners(): void {
        if (this._uploaderListenerStart) this._uploader.onStart.remove(this._uploaderListenerStart);
        if (this._uploaderListenerProgress) this._uploader.onProgress.remove(this._uploaderListenerProgress);
        if (this._uploaderListenerError) this._uploader.onError.remove(this._uploaderListenerError);
        if (this._uploaderListenerFinished) this._uploader.onFinished.remove(this._uploaderListenerFinished);
        if (this._uploaderListenerCancel) this._uploader.onCancel.remove(this._uploaderListenerCancel);
    }

    //#region Zpracování metod z globální třídy GenericControl
    /**
     * Zaregistruje událost
     * @param event Název události
     * @param action Akce při vyvolání události
     */
    public registerEventListener(event: string, action: (e: Event) => void): void {
        GenericControl.registerEventListener(this as HTMLElement, event, action);
    }

    /**
     * Odregistruje udalost. Pokud neni vyplnen zadny parametr, jsou odregistrovany vsechny udalosti. Pokud je vyplnen pouze event, odregistruje vsechny udalosti daneho typu
     * pokud je vyplnen event a action, odregistruje pouze konkretni akci
     * @param event Event, ktery chcete odregistrovat.
     * @param action Akce, ktere se tyka event a chcete ji odregistrovat
     */
    public unregisterEventListener(event?: string, action?: (e: Event) => void): void {
        GenericControl.unregisterEventListener(this as HTMLElement, event, action);
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    public callEvent(eventName: string, detail?: any, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        return GenericControl.callEvent(this as HTMLElement, eventName, detail, bubbles, cancelable);
    }

    /**
     * Vrátí pozici elementu v rámci dokumentu
     */
    public offset(): { top: number; left: number } {
        return GenericControl.offset(this as HTMLElement);
    }

    //#endregion Zpracování metod z globální třídy GenericControl

    public dispose(): void {
        if (this._disposed)
            return;
        this._disposed = true;
        GenericControl.unregisterEventListener(this._row);
        this.unregisterEventListener();
        this._unregisterUploadListeners();
        if (this._signStatusBtn && this._signInfoBtnClickListener)
            this._signStatusBtn.element.removeEventListener("click", this._signInfoBtnClickListener);
        if (this._objectAttributeChangeListener && this._actualObject) {
            this._actualObject.attributeChanged.remove(this._objectAttributeChangeListener);
            this._objectAttributeChangeListener = null;
        }
    }
}

registerCustomElement("tsc-fileeditcell", FileCellElement, "div");
